/**
 * UserDashboard persona permissions — keyed by Customer `role`.
 * No separate CLIENT_ROLES layer; legacy `user` normalizes to ADMIN.
 */

export const HOSPITAL_ROLES = [
  'IPC_LEAD',
  'QUALITY',
  'EVS_SUP',
  'EVS_STAFF',
  'OPERATOR',
  'ADMIN',
];

const ROLE_SET = new Set(HOSPITAL_ROLES);

/** @type {Record<string, string[]>} */
const ROLE_PERMISSIONS = {
  IPC_LEAD: [
    'nav:overview',
    'nav:reports',
    'nav:operations',
    'nav:rooms',
    'nav:efficacy',
    'reports:read',
    'reports:write',
    'reports:export',
    'exceptions:read',
    'exceptions:write',
    'followups:read',
    'followups:write',
    'manual_clean:write',
    'verification:write',
    'compliance:read',
  ],
  QUALITY: [
    'nav:overview',
    'nav:reports',
    'nav:operations',
    'nav:rooms',
    'nav:efficacy',
    'reports:read',
    'reports:write',
    'reports:export',
    'exceptions:read',
    'exceptions:write',
    'followups:read',
    'followups:write',
    'manual_clean:write',
    'verification:write',
    'compliance:read',
  ],
  EVS_SUP: [
    'nav:overview',
    'nav:reports',
    'nav:operations',
    'nav:rooms',
    'nav:efficacy',
    'nav:robot',
    'reports:read',
    'reports:write',
    'reports:export',
    'exceptions:read',
    'exceptions:write',
    'followups:read',
    'followups:write',
    'manual_clean:write',
    'verification:write',
    'rooms:assign',
  ],
  EVS_STAFF: [
    'nav:rooms',
    'nav:reports',
    'reports:read',
    'exceptions:read',
    'followups:read',
    'manual_clean:write',
    'verification:write',
    'rooms:today',
  ],
  OPERATOR: [
    'nav:overview',
    'nav:operations',
    'nav:rooms',
    'nav:robot',
    'operations:write',
    'rooms:readiness',
    'followups:read',
  ],
  ADMIN: [
    'nav:overview',
    'nav:reports',
    'nav:operations',
    'nav:rooms',
    'nav:efficacy',
    'nav:subscription',
    'nav:robot',
    'nav:members',
    'reports:read',
    'reports:export',
    'exceptions:read',
    'followups:read',
    'compliance:read',
    'compliance:write',
    'rooms:master',
    'subscription:write',
    'members:write',
  ],
};

/**
 * Normalize Customer role for permission checks.
 * Legacy forced `"user"` → ADMIN (full access for existing single-user tenants).
 */
export function normalizeHospitalRole(role) {
  const r = String(role ?? '').trim();
  if (!r || r.toLowerCase() === 'user') return 'ADMIN';
  if (ROLE_SET.has(r)) return r;
  return 'ADMIN';
}

export function isHospitalRole(role) {
  const r = String(role ?? '').trim();
  if (r.toLowerCase() === 'user') return true;
  return ROLE_SET.has(r);
}

export function getPermissionsForRole(role) {
  return ROLE_PERMISSIONS[normalizeHospitalRole(role)] || ROLE_PERMISSIONS.ADMIN;
}

export function can(role, permission) {
  const perms = getPermissionsForRole(role);
  if (perms.includes('*')) return true;
  if (perms.includes(permission)) return true;
  const [resource] = String(permission).split(':');
  return perms.includes(`${resource}:*`);
}

export function canAny(role, permissions = []) {
  return permissions.some((p) => can(role, p));
}

/** Default post-login / unauthorized redirect inside UserDashboard. */
export function defaultLandingPath(role) {
  const r = normalizeHospitalRole(role);
  switch (r) {
    case 'IPC_LEAD':
    case 'QUALITY':
      return '/client/reports';
    case 'EVS_SUP':
      return '/client/reports';
    case 'EVS_STAFF':
      return '/client/rooms';
    case 'OPERATOR':
      return '/client/operations';
    case 'ADMIN':
    default:
      return '/client/overview';
  }
}

/** Nav `sub` / clientPath → required permission */
export const NAV_PERMISSION_BY_KEY = {
  overview: 'nav:overview',
  reports: 'nav:reports',
  operations: 'nav:operations',
  rooms: 'nav:rooms',
  efficacy: 'nav:efficacy',
  subscription: 'nav:subscription',
  robot: 'nav:robot',
  members: 'nav:members',
};

export function hospitalRoleLabel(role) {
  const r = normalizeHospitalRole(role);
  const labels = {
    IPC_LEAD: 'Infection Control Lead',
    QUALITY: 'Quality / Accreditation',
    EVS_SUP: 'EVS Supervisor',
    EVS_STAFF: 'Housekeeping Staff',
    OPERATOR: 'Robot Operator',
    ADMIN: 'Hospital Admin',
  };
  return labels[r] || r;
}
